import React, {useRef} from 'react';
import PageTitle from '../../../../uiToolkit/PageTitle';
import './styles.css';
import {Row, Col, Alert} from 'react-bootstrap';
import {isEmail} from 'react-multi-email';
import {learnersText} from '../../learnersText';
import Button from '../../../../uiToolkit/Buttons/Button';
import {getErrorFromCode} from '../../../../data/errorCodes';
import 'react-multi-email/dist/style.css';
import MultiInput from '../../../../uiToolkit/Inputs/MultiInput';
import BackButton from '../../../../uiToolkit/Buttons/BackButton';
import {useNavigate, useParams} from 'react-router-dom';

const ALLOWED_FILE_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

interface Props {
  onSubmit: (learners: string[]) => void;
  onCancel: () => void;
  onFileUpload: (file: File) => void;
  learners: string[];
  setLearners: (learners: string[]) => void;
  isLoading: boolean;
  failedInvitations: any;
}

const CreateLearnersUI = React.memo(
  ({
    onSubmit,
    onFileUpload,
    learners,
    setLearners,
    isLoading,
    failedInvitations,
  }: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();
    const {organizationId} = useParams();

    const buttonEnabled = () => learners.length > 0;

    const onSave = () => {
      onSubmit(learners);
    };

    const getFailedReasons = () => {
      if (failedInvitations) {
        return Object.values(failedInvitations).filter(
          (value, index, array) => {
            return array.indexOf(value) === index;
          },
        );
      }
      return [];
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        if (
          ALLOWED_FILE_TYPES.includes(file.type) ||
          file.name.endsWith('.csv') ||
          file.name.endsWith('.xlsx')
        ) {
          onFileUpload(file);
        } else {
          alert(learnersText.invalidFileFormat);
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    const handleDownloadTemplate = () => {
      const link = document.createElement('a');
      link.href = '/templates/learners-import-template.csv';
      link.download = 'learners-import-template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const onBack = () => {
      navigate(`/manage/${organizationId}/learners`, {replace: true});
    };
    return (
      <>
        <div className="HeaderContainerLearner">
          <BackButton overrideOnBack={onBack} />
          <PageTitle
            title={learnersText.inviteLearnersPageTitle}
            subtitle={learnersText.inviteLearnersPageSubtitle}
          />
        </div>
        <Row className="justify-content-center">
          <Col lg={8} xs={12}>
            <div className="AddLearnersContainer">
              <div className="ManualImport">
                <p className="ImportHeader">{learnersText.emailImport}</p>

                <div className="Input">
                  <MultiInput
                    validator={isEmail}
                    value={learners}
                    setValue={setLearners}
                    heading={learnersText.HeadingInviteLearnerInputField}
                    placeholder={learnersText.inputPlaceHolder}
                  />
                </div>
                <div className="Footer">
                  <Button
                    onClick={onSave}
                    title={learnersText.SubmitButton}
                    disabled={!buttonEnabled()}
                    isLoading={isLoading}
                    uiType="action"
                  />
                </div>
              </div>
              <p className="ImportSeparator">OR</p>
              <div className="ImportSection">
                <p className="ImportHeader">{learnersText.orImportFromFile}</p>
                <div className="ImportActions">
                  <label
                    className={`FileInputLabel ${isLoading ? 'disabled' : ''}`}>
                    {learnersText.importFromFile}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx"
                      onChange={handleFileChange}
                      className="HiddenFileInput"
                      disabled={isLoading}
                    />
                  </label>
                  <p>
                    <span
                      className="DownloadLink"
                      onClick={handleDownloadTemplate}>
                      {learnersText.downloadTemplateOne}
                    </span>
                    <span>{learnersText.downloadTemplateTwo}</span>
                  </p>
                </div>
              </div>
              <div className="FailedEmailsContainer">
                {getFailedReasons().map((reason: any) => (
                  <Alert key={reason.toString()} variant="warning">
                    <p className="FailedEmailReason">
                      {getErrorFromCode(reason)}
                    </p>

                    {Object.keys(failedInvitations)
                      .filter(x => failedInvitations[x] === reason)
                      .map(item => (
                        <p className="FailedEmailError" key={item}>
                          {item}
                        </p>
                      ))}
                  </Alert>
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </>
    );
  },
);

export default CreateLearnersUI;
